/**
 * Sentry para el portal cliente.
 *
 * Init lazy — si VITE_SENTRY_DSN_PORTAL no está, no se inicializa y todas
 * las llamadas son no-op.
 *
 * Privacidad: sendDefaultPii=false, sin replay, sin auto IP/email al scope.
 * Solo se etiqueta al user con id (no email) después de login.
 */

import * as Sentry from "@sentry/react";

let initialized = false;

export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN_PORTAL || import.meta.env.VITE_SENTRY_DSN;
  if (!dsn || initialized) return;

  try {
    Sentry.init({
      dsn,
      environment:        import.meta.env.MODE,
      release:            import.meta.env.VITE_APP_VERSION || "unknown",
      tracesSampleRate:   parseFloat(import.meta.env.VITE_SENTRY_TRACES_RATE || "0.15"),
      sendDefaultPii:     false,

      // Replay desactivado — el portal maneja datos de clientes reales
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 0,

      // Filtros para no llenar la cuota con ruido
      beforeSend(event) {
        const msg = event.message || event.exception?.values?.[0]?.value || "";
        if (msg.includes("ResizeObserver loop"))            return null;
        if (msg.includes("Non-Error promise rejection"))    return null;
        if (msg.match(/network.*error/i))                   return null;   // offline transitorio
        if (msg.includes("Unauthorized"))                   return null;   // token expiró
        if (msg.includes("Failed to fetch"))                return null;   // fetch abortado
        if (msg.includes("AbortError"))                     return null;
        return event;
      },
    });
    initialized = true;
    if (import.meta.env.DEV) {
      console.log("[Sentry Portal] Inicializado", { env: import.meta.env.MODE });
    }
  } catch (err) {
    console.warn("[Sentry Portal] Init falló:", err.message);
  }
}

/**
 * Etiqueta al usuario logueado. Solo id — sin email por privacidad.
 * El backend maneja los emails; acá alcanza con el user_id para correlacionar.
 */
export function setSentryUser(user) {
  if (!initialized || !user) return;
  Sentry.setUser({ id: user.id || user.uid || user.user_id });
}

export function clearSentryUser() {
  if (!initialized) return;
  Sentry.setUser(null);
}

export const ErrorBoundary = Sentry.ErrorBoundary;

export function captureException(err, context = {}) {
  if (!initialized) {
    console.error("[capture]", err, context);
    return;
  }
  Sentry.withScope(scope => {
    Object.entries(context).forEach(([k, v]) => scope.setExtra(k, v));
    Sentry.captureException(err);
  });
}

export function isInitialized() {
  return initialized;
}
